// Thin wrapper around MediaRecorder. Owns the state machine
// (idle → recording → paused → stopped), duration tracking across
// pause intervals, and mimeType negotiation. The UI layer creates
// one Recorder per take and tears it down on completion; this file
// has no module-level state so multiple recorders can run sequentially
// without leakage.
//
// Why a factory: player.ts is a singleton because Strudel is. A
// recorder is per-take, so an instance pattern fits better and keeps
// test isolation simple.

export type RecorderState = 'idle' | 'recording' | 'paused' | 'stopped';

export type RecorderEvent =
  | { type: 'state'; state: RecorderState }
  | { type: 'duration'; ms: number }
  | { type: 'complete'; blob: Blob; mimeType: string; durationMs: number }
  | { type: 'error'; error: Error };

export type RecorderOptions = {
  /** Ordered candidate mime types. First supported one is used.
   *  Defaults to webm/opus → webm → mp4 → '' (browser default). */
  mimeTypes?: string[];
  /** How often to emit `duration` events while recording. Default 100ms. */
  durationTickMs?: number;
  /** MediaRecorder `timeslice` — passed to rec.start(). Bounds the per-
   *  chunk blob size so long recordings don't accumulate a single giant
   *  buffer. Default 2000ms; pass 0 to buffer to stop. */
  timesliceMs?: number;
  /** Monotonic clock — override for deterministic tests. */
  now?: () => number;
  /** MediaRecorder constructor — override for tests that don't have a
   *  real one. */
  recorderCtor?: typeof MediaRecorder;
  /** Timer helpers — override for tests. */
  setInterval?: (fn: () => void, ms: number) => number;
  clearInterval?: (id: number) => void;
};

export type Recorder = {
  state: () => RecorderState;
  durationMs: () => number;
  mimeType: () => string;
  start: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  /** Stop without emitting 'complete'. The captured blob is discarded
   *  and no take is added. State transitions to 'stopped' like a normal
   *  stop so consumers clear their refs. */
  cancel: () => void;
  onEvent: (handler: (e: RecorderEvent) => void) => () => void;
};

const DEFAULT_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  '',
];

export function createRecorder(
  stream: MediaStream,
  options: RecorderOptions = {},
): Recorder {
  const now = options.now ?? (() => Date.now());
  const setIntervalFn = options.setInterval ?? ((fn, ms) => window.setInterval(fn, ms));
  const clearIntervalFn =
    options.clearInterval ?? ((id) => window.clearInterval(id));
  const RecorderCtor = options.recorderCtor ?? pickGlobalRecorder();
  const tickMs = options.durationTickMs ?? 100;
  const timesliceMs = options.timesliceMs ?? 2000;

  const handlers = new Set<(e: RecorderEvent) => void>();
  const chunks: Blob[] = [];
  let state: RecorderState = 'idle';
  let recorder: MediaRecorder | null = null;
  let mime = '';
  let startedAt = 0;
  let pausedAt = 0;
  let pausedAccum = 0;
  let stoppedAt = 0;
  let tickerId: number | null = null;
  // Set by cancel() so the onstop handler knows to skip the 'complete'
  // emission. Chunks accumulated so far are dropped.
  let cancelled = false;

  function emit(event: RecorderEvent): void {
    for (const h of handlers) {
      try {
        h(event);
      } catch {
        // Handlers shouldn't throw; if they do, don't cascade.
      }
    }
  }

  function setState(next: RecorderState): void {
    if (state === next) return;
    state = next;
    emit({ type: 'state', state: next });
  }

  function computeDuration(): number {
    if (state === 'idle') return 0;
    const end = state === 'stopped' ? stoppedAt : now();
    const activePause = state === 'paused' ? end - pausedAt : 0;
    return Math.max(0, end - startedAt - pausedAccum - activePause);
  }

  function startTicker(): void {
    if (tickerId !== null) return;
    tickerId = setIntervalFn(() => {
      emit({ type: 'duration', ms: computeDuration() });
    }, tickMs);
  }

  function stopTicker(): void {
    if (tickerId !== null) {
      clearIntervalFn(tickerId);
      tickerId = null;
    }
  }

  function pickMime(): string {
    const candidates = options.mimeTypes ?? DEFAULT_MIME_CANDIDATES;
    if (!RecorderCtor || typeof RecorderCtor.isTypeSupported !== 'function') {
      return candidates[0] ?? '';
    }
    for (const m of candidates) {
      if (m === '' || RecorderCtor.isTypeSupported(m)) return m;
    }
    return '';
  }

  return {
    state: () => state,
    durationMs: computeDuration,
    mimeType: () => mime,

    start: () => {
      if (state !== 'idle') return;
      if (!RecorderCtor) {
        emit({ type: 'error', error: new Error('MediaRecorder not available in this browser') });
        return;
      }
      try {
        mime = pickMime();
        const rec: MediaRecorder =
          mime === ''
            ? new RecorderCtor(stream)
            : new RecorderCtor(stream, { mimeType: mime });
        recorder = rec;

        rec.ondataavailable = (ev: BlobEvent) => {
          if (ev.data && ev.data.size > 0) chunks.push(ev.data);
        };
        rec.onstop = () => {
          stoppedAt = now();
          stopTicker();
          setState('stopped');
          if (cancelled) return;
          const finalMime = mime || (chunks[0]?.type ?? 'audio/webm');
          const blob = new Blob(chunks, { type: finalMime });
          emit({
            type: 'complete',
            blob,
            mimeType: finalMime,
            durationMs: computeDuration(),
          });
        };
        rec.onerror = (ev: Event) => {
          const err =
            (ev as unknown as { error?: Error }).error ??
            new Error('MediaRecorder error');
          emit({ type: 'error', error: err });
        };

        startedAt = now();
        pausedAt = 0;
        pausedAccum = 0;
        stoppedAt = 0;
        if (timesliceMs > 0) rec.start(timesliceMs);
        else rec.start();
        setState('recording');
        startTicker();
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        emit({ type: 'error', error: err });
      }
    },

    pause: () => {
      if (state !== 'recording' || !recorder) return;
      try {
        recorder.pause();
        pausedAt = now();
        setState('paused');
      } catch (e) {
        emit({ type: 'error', error: e instanceof Error ? e : new Error(String(e)) });
      }
    },

    resume: () => {
      if (state !== 'paused' || !recorder) return;
      try {
        recorder.resume();
        pausedAccum += now() - pausedAt;
        pausedAt = 0;
        setState('recording');
      } catch (e) {
        emit({ type: 'error', error: e instanceof Error ? e : new Error(String(e)) });
      }
    },

    stop: () => {
      if (state !== 'recording' && state !== 'paused') return;
      try {
        // If paused, account for the trailing pause before stopping so
        // duration math is stable regardless of which state stop() fires
        // from.
        if (state === 'paused') {
          pausedAccum += now() - pausedAt;
          pausedAt = 0;
        }
        recorder?.stop();
      } catch (e) {
        emit({ type: 'error', error: e instanceof Error ? e : new Error(String(e)) });
      }
    },

    cancel: () => {
      if (state !== 'recording' && state !== 'paused') return;
      cancelled = true;
      try {
        recorder?.stop();
      } catch (e) {
        emit({ type: 'error', error: e instanceof Error ? e : new Error(String(e)) });
      }
    },

    onEvent: (handler) => {
      handlers.add(handler);
      return () => {
        handlers.delete(handler);
      };
    },
  };
}

function pickGlobalRecorder(): typeof MediaRecorder | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return MediaRecorder;
}
